import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useToast, Toast, ToastType } from '../hooks/useToast';
import ToastComponent from '../components/Toast';
import ErrorModal from '../components/ErrorModal';

interface ModalErrorState {
  isOpen: boolean;
  error: string;
  title: string;
  severity: 'error' | 'warning' | 'info';
  showRetry: boolean;
  onRetry?: () => void;
  guidance?: string;
}

interface ShowErrorModalOptions {
  message?: string;
  error?: string;
  title?: string;
  severity?: 'error' | 'warning' | 'info';
  showRetry?: boolean;
  onRetry?: () => void;
  guidance?: string;
}

interface ToastOptions {
  duration?: number;
  guidance?: string;
  [key: string]: any;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (message: string, type: ToastType, options?: ToastOptions) => number;
  showSuccess: (message: string, options?: ToastOptions) => number;
  showError: (message: string, options?: ToastOptions) => number;
  showInfo: (message: string, options?: ToastOptions) => number;
  showWarning: (message: string, options?: ToastOptions) => number;
  removeToast: (id: number) => void;
  showErrorModal: (options: ShowErrorModalOptions) => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * トースト機能を提供するコンテキストプロバイダー
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const toastHook = useToast();
  const [modalError, setModalError] = useState<ModalErrorState | null>(null);

  /**
   * 重要なエラーをモーダルで表示
   * @param {ShowErrorModalOptions} options - モーダル設定
   */
  const showErrorModal = (options: ShowErrorModalOptions): void => {
    setModalError({
      isOpen: true,
      error: options.message || options.error || '',
      title: options.title || 'エラーが発生しました',
      severity: options.severity || 'error',
      showRetry: options.showRetry || false,
      onRetry: options.onRetry,
      guidance: options.guidance
    });
  };

  const closeErrorModal = (): void => {
    setModalError(null);
  };

  const value: ToastContextValue = {
    toasts: toastHook.toasts,
    showToast: toastHook.showToast,
    showSuccess: toastHook.showSuccess,
    showError: toastHook.showError,
    showInfo: toastHook.showInfo,
    showWarning: toastHook.showWarning,
    removeToast: toastHook.removeToast,
    showErrorModal
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* トースト通知の表示 */}
      <ToastComponent toasts={toastHook.toasts} onRemove={toastHook.removeToast} />

      {/* エラーモーダルの表示 */}
      {modalError && (
        <ErrorModal
          isOpen={modalError.isOpen}
          onClose={closeErrorModal}
          error={modalError.error}
          title={modalError.title}
          severity={modalError.severity}
          showRetry={modalError.showRetry}
          onRetry={modalError.onRetry}
          guidance={modalError.guidance}
        />
      )}
    </ToastContext.Provider>
  );
};

/**
 * トースト機能を使用するためのフック
 */
export const useToastContext = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};
